/**
 * The refund promise, in one place.
 *
 * It used to live in five, and drifted into four different promises: "30-day
 * money-back guarantee" hardcoded at the point of purchase (EN and ES), a
 * "30-Day Guarantee" trust badge on the demo, "within 7 days... no questions
 * asked" in the FAQ, and a live policy still keyed on a product this site does
 * not sell. The most generous one sat on the checkout modal, which is the
 * representation a buyer actually relies on.
 *
 * The promise itself changed shape too. A change-of-mind guarantee exists to
 * substitute for evidence the buyer cannot get before paying; the free scan
 * already shows them how many issues their site has, so the substitute is
 * redundant on an artefact that is delivered in minutes and cannot be returned.
 * What replaces it is a fulfilment guarantee whose conditions are all checkable
 * in the database (status, findings, url, report_pdf_url), which is also what
 * gives a defensible answer in a payment dispute.
 *
 * `guarantee.test.ts` asserts every surface still agrees. The long form lives in
 * the `audit.faq` and `legal.refunds` i18n namespaces; keep all of them in step.
 */

export type GuaranteeLocale = 'en' | 'es';

export const GUARANTEE = {
  en: {
    /** Under the price in the upgrade card. */
    terms: 'One-time payment • Instant delivery • Full refund if the report fails',
    /** Under the pay button in the checkout modal. */
    checkout: 'Full refund if the report fails to generate • Secure payment via Polar',
    /** Trust badge, demo report. */
    badge: 'Refund if it fails',
  },
  es: {
    terms: 'Pago único • Entrega inmediata • Reembolso completo si el reporte falla',
    checkout: 'Reembolso completo si el reporte falla • Pago seguro vía Polar',
    badge: 'Reembolso si falla',
  },
} as const;

export function guaranteeCopy(locale: string) {
  return GUARANTEE[(locale === 'es' ? 'es' : 'en') as GuaranteeLocale];
}
